import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, Timestamp, where, writeBatch } from 'firebase/firestore';

import { db } from '@/config/firebase';
import type {
  CreateFoodBenefitImportPayload,
  FoodBenefitCodeAssignment,
  FoodBenefitImportDocument,
  FoodBenefitImportTotals,
  FoodBenefitImportRecordPreview,
  FoodBenefitSourceFile,
  FoodBenefitAuditItem,
} from './foodBenefitTypes';
import { FOOD_BENEFIT_DISTRIBUTION_KEYS, createEmptyFoodBenefitDistribution } from './foodBenefitTypes';

const FOOD_BENEFIT_IMPORTS_COLLECTION = 'expenses_food_benefit_imports';
const FOOD_BENEFIT_CODES_COLLECTION = 'expenses_food_benefit_codes';

function normalizeCode(code: string): string {
  return code?.toString().trim().replace(/\s+/g, '') ?? '';
}

const createEmptyTotals = (): FoodBenefitImportTotals => ({
  dependents: 0,
  feeCount: 0,
  amount: 0,
  breakdown: createEmptyFoodBenefitDistribution(),
});

export async function createFoodBenefitImport(
  provider: string,
  payload: CreateFoodBenefitImportPayload,
  user: { uid: string; email?: string | null; displayName?: string | null },
): Promise<FoodBenefitImportDocument> {
  const totals = payload.totals ?? createEmptyTotals();
  const docRef = await addDoc(collection(db, FOOD_BENEFIT_IMPORTS_COLLECTION), {
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals,
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    orders: payload.orders ?? [],
    audit: payload.audit ?? [],
    createdAt: serverTimestamp(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
  });

  return {
    id: docRef.id,
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals,
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    orders: payload.orders ?? [],
    audit: payload.audit ?? [],
    createdAt: new Date(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
  };
}

export async function listFoodBenefitCodeAssignments(): Promise<Record<string, string>> {
  const snapshot = await getDocs(collection(db, FOOD_BENEFIT_CODES_COLLECTION));
  const assignments: Record<string, string> = {};
  snapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    const collaboratorId = typeof data?.collaboratorId === 'string' ? data.collaboratorId : '';
    if (!collaboratorId) return;
    assignments[docSnapshot.id] = collaboratorId;
  });
  return assignments;
}

export async function saveFoodBenefitCodeAssignments(assignments: FoodBenefitCodeAssignment[]): Promise<void> {
  const normalized = assignments
    .map((assignment) => {
      const normalizedCode = normalizeCode(assignment.code);
      if (!normalizedCode) return null;
      return { code: normalizedCode, collaboratorId: assignment.collaboratorId };
    })
    .filter(Boolean) as FoodBenefitCodeAssignment[];

  if (!normalized.length) return;

  const batch = writeBatch(db);
  normalized.forEach(({ code, collaboratorId }) => {
    const ref = doc(db, FOOD_BENEFIT_CODES_COLLECTION, code);
    batch.set(ref, { collaboratorId, updatedAt: serverTimestamp() }, { merge: true });
  });

  await batch.commit();
}

export async function deleteFoodBenefitImportById(importId: string): Promise<void> {
  const recordsSnap = await getDocs(collection(db, FOOD_BENEFIT_IMPORTS_COLLECTION, importId, 'records'));

  let batch = writeBatch(db);
  let operations = 0;
  for (const recordDoc of recordsSnap.docs) {
    batch.delete(recordDoc.ref);
    operations += 1;
    if (operations >= 400) {
      await batch.commit();
      batch = writeBatch(db);
      operations = 0;
    }
  }
  if (operations > 0) {
    await batch.commit();
  }

  await deleteDoc(doc(db, FOOD_BENEFIT_IMPORTS_COLLECTION, importId));
}

function mapTotals(data: any): FoodBenefitImportTotals {
  const breakdown = createEmptyFoodBenefitDistribution();
  if (data && typeof data === 'object') {
    const sourceBreakdown = typeof data.breakdown === 'object' && data.breakdown ? data.breakdown : {};
    FOOD_BENEFIT_DISTRIBUTION_KEYS.forEach((key) => {
      const value = sourceBreakdown[key];
      breakdown[key] = typeof value === 'number' ? value : 0;
    });
  }
  return {
    dependents: typeof data?.dependents === 'number' ? data.dependents : 0,
    feeCount: typeof data?.feeCount === 'number' ? data.feeCount : 0,
    amount: typeof data?.amount === 'number' ? data.amount : 0,
    breakdown,
  };
}

function mapSourceFiles(data: any): FoodBenefitSourceFile[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => ({
      name: typeof item?.name === 'string' ? item.name : 'unknown',
      size: typeof item?.size === 'number' ? item.size : 0,
      contentType: typeof item?.contentType === 'string' ? item.contentType : null,
    }))
    .filter((file) => file.name);
}

function mapDocument(docSnapshot: any): FoodBenefitImportDocument | null {
  const data = docSnapshot.data?.() ?? docSnapshot.data;
  if (!data) return null;
  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null;
  return {
    id: docSnapshot.id,
    provider: typeof data.provider === 'string' ? data.provider : '',
    billingPeriod: typeof data.billingPeriod === 'string' ? data.billingPeriod : null,
    totals: mapTotals(data.totals),
    sourceFiles: mapSourceFiles(data.sourceFiles),
    recordsPreview: Array.isArray(data.recordsPreview) ? (data.recordsPreview as any[]) : [],
    orders: Array.isArray(data.orders) ? (data.orders as any[]) : [],
    audit: Array.isArray(data.audit) ? (data.audit as FoodBenefitAuditItem[]) : [],
    createdAt,
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    createdByEmail: typeof data.createdByEmail === 'string' ? data.createdByEmail : null,
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : null,
  };
}

export async function listFoodBenefitImports(): Promise<FoodBenefitImportDocument[]> {
  const importsQuery = query(collection(db, FOOD_BENEFIT_IMPORTS_COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(importsQuery);
  return snapshot.docs.map(mapDocument).filter(Boolean) as FoodBenefitImportDocument[];
}

export async function listFoodBenefitImportsByCompetence(competence: string): Promise<FoodBenefitImportDocument[]> {
  const importsQuery = query(collection(db, FOOD_BENEFIT_IMPORTS_COLLECTION), where('billingPeriod', '==', competence));
  const snapshot = await getDocs(importsQuery);
  return (snapshot.docs.map(mapDocument).filter(Boolean) as FoodBenefitImportDocument[]).sort(
    (a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0),
  );
}

export async function saveFoodBenefitImportRecords(
  importId: string,
  records: FoodBenefitImportRecordPreview[],
): Promise<void> {
  if (!records.length) return;

  const recordsCollection = collection(db, FOOD_BENEFIT_IMPORTS_COLLECTION, importId, 'records');
  let batch = writeBatch(db);
  let operations = 0;

  for (const record of records) {
    const recordRef = doc(recordsCollection);
    batch.set(recordRef, {
      ...record,
      collaboratorId: record.collaboratorId ?? null,
      createdAt: serverTimestamp(),
    });
    operations += 1;
    if (operations >= 400) {
      await batch.commit();
      batch = writeBatch(db);
      operations = 0;
    }
  }

  if (operations > 0) {
    await batch.commit();
  }
}
